"use no memo";

/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash-es';

import { getItemsFromMenuData } from 'helpers/menu';
import { getFlatModIds, mapIdToName } from 'helpers/order';

import {
  MenuCategoriesType,
  MenuDataType,
  MenuItemType,
  ModifierCategoryType,
  ModifierType,
} from 'types/menu';

interface MenuProviderProps {
  children: React.ReactNode;
}

export type MenuContextProps = {
  menu: MenuDataType;
  menuCategoriesWithItem: MenuCategoriesType[];
  menuItems: MenuItemType[];
  isMenuLoading: boolean;
  getChildModifiers: (branch: {
    modifier_categories?: ModifierCategoryType[];
  }) => string[];
  getCompleteModifierDataForItem: (id: string) => ModifierCategoryType[];
  getNestedModData: (id: string) => ModifierCategoryType[];
  getParentModifierName: (id: string) => string;
  getSizeDataForItem: (id: string) => MenuItemType[];
  setMenu: (data: MenuDataType) => void;
  setIsMenuLoading: (isLoading: boolean) => void;
};

const MenuContext = createContext<MenuContextProps>({
  menu: {},
  menuCategoriesWithItem: [],
  menuItems: [],
  isMenuLoading: false,
  getChildModifiers: () => [],
  getCompleteModifierDataForItem: () => [],
  getNestedModData: () => [],
  getParentModifierName: () => '',
  getSizeDataForItem: () => [],
  setMenu: () => {},
  setIsMenuLoading: () => {},
});

function MenuProvider(props: MenuProviderProps) {
  const { children } = props;

  const [menuState, setMenuState] = useState<MenuDataType>({});
  const [isLoading, setIsLoading] = useState(false);
  const [menuCategoriesWithItem, setMenuCategoriesWithItems] = useState<
    MenuCategoriesType[]
  >([]);
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [modifiers, setModifiers] = useState<ModifierType[]>([]);

  function getMenuCategoriesWithItems() {
    const filteredCategories = menuState.menu_categories?.map((category) => {
      const filteredItems: MenuItemType[] = [];
      const servingSizes: string[] = [];
      category.items.forEach((item) => {
        const servingSizeGroup = item.meta_item_details?.serving_size_group;
        if (servingSizeGroup) {
          const defaultSizeId = servingSizeGroup.default_id;
          const defaultSize =
            servingSizeGroup.child_items.find(
              (catItem) => catItem.id === defaultSizeId
            ) || servingSizeGroup.child_items[0];

          filteredItems.push({ ...item, price: defaultSize.price });
          servingSizeGroup.child_items.forEach((child) =>
            servingSizes.push(child.id)
          );
        } else if (!servingSizes.includes(item.id)) {
          filteredItems.push({ ...item });
        }
      });

      return { ...category, items: filteredItems };
    });

    setMenuCategoriesWithItems(filteredCategories || []);
  }

  function getMenuItems() {
    const fullItems = getItemsFromMenuData(menuState);
    setMenuItems(fullItems);
    setModifiers(menuState.modifiers || []);
  }

  function getCompleteModifierDataForItem(itemId: string) {
    if (isEmpty(menuState)) return [];

    const menuItem = menuItems.find((item) => item.id === itemId);
    const modifierCategoriesForItem = menuItem?.modifier_categories;

    if (!modifierCategoriesForItem) return [];

    const completedModData = modifierCategoriesForItem.map(
      (modifierCategoryId) => {
        const modCat = menuState.modifier_categories!.find(
          (category) => category.id === modifierCategoryId
        )!;
        const mods = modCat.modifiers.map(
          (modifierId) =>
            menuState.modifiers!.find((mod) => mod.id === modifierId)!
        );

        return { ...modCat, modifiers: mods };
      }
    );
    return completedModData;
  }

  function getNestedModData(itemId: string) {
    if (isEmpty(menuState)) return [];

    const modifier = modifiers.find((item) => item.id === itemId);
    const modifierCategoriesForItem = modifier?.modifier_categories;

    if (!modifierCategoriesForItem) return [];

    const modData = modifierCategoriesForItem.map((modifierCategoryId) => {
      const modCat = menuState.modifier_categories!.find(
        (category) => category.id === modifierCategoryId
      )!;
      const mods = modCat.modifiers.map(
        (modifierId: string) =>
          menuState.modifiers!.find((mod) => mod.id === modifierId)!
      );
      return { ...modCat, modifiers: mods };
    });
    return modData;
  }

  function getSizeDataForItem(itemId: string) {
    const menuItem = menuItems.find((item) => item.id === itemId);
    const servingSizeGroup = menuItem?.meta_item_details?.serving_size_group;
    return servingSizeGroup?.child_items || [];
  }

  function getChildModifiers(branch: {
    modifier_categories?: ModifierCategoryType[];
  }) {
    const nestedIds = getFlatModIds(branch?.modifier_categories) || [];
    const nestedNames: string[] = nestedIds.map(
      (id) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        mapIdToName(id, modifiers) // can remove eslint disable with conversion of order helpers
    );
    return nestedNames;
  }

  function getParentModifierName(parentId: string) {
    const modifier = modifiers.find((item) => item.id === parentId);
    return modifier?.name || '';
  }

  useEffect(() => {
    if (!menuState.menu_categories) return;

    getMenuCategoriesWithItems();
    getMenuItems();
  }, [menuState]);

  return (
    <MenuContext.Provider
      value={{
        menu: menuState,
        menuCategoriesWithItem,
        menuItems,
        isMenuLoading: isLoading,
        setMenu: setMenuState,
        setIsMenuLoading: setIsLoading,
        getCompleteModifierDataForItem,
        getNestedModData,
        getSizeDataForItem,
        getChildModifiers,
        getParentModifierName,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}

MenuProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { MenuContext, MenuProvider };
